import botSchema from '@feedyou/schema/schema/botFlow.json'
import { NlpProviderType } from '@feedyou/schema'
import { UiSchema } from '@rjsf/utils'
import {
  HIDDEN_WIDGET,
  RADIO_WIDGET,
  RANGE_WIDGET,
  TEXTAREA_WIDGET,
  WITHOUT_LABEL
} from '@features/schema'
import { useIsStandaloneKbEnabled } from '@features/knowledgeBase'

import { WithModelSpecificPropertyWarning } from '../../../../design-system/RjsfDescriptionLinks/variants'
import { useFeature } from '../../../../../store/features'

const nlpProviderSchemas =
  botSchema.properties.nlp.properties.models.items.properties.provider.anyOf

export function useUiSchema(
  providerType?: NlpProviderType,
  defaultPrompt?: string,
  showSources?: string
): UiSchema {
  const isNlpEntityRepositoryEnabled = !!useFeature('NLP_CUSTOM_ENTITY_OPTION_REPOSITORY')
  const isStandaloneKbEnabled = useIsStandaloneKbEnabled()

  const providerTypesWithHiddenDefaultDialog: NlpProviderType[] = ['kb-chat', 'openai-chat']

  return {
    items: {
      ...WITHOUT_LABEL,
      allowedDialogs: {
        items: {
          ...WITHOUT_LABEL,
          'ui:classNames': 'rjsf-array-select',
          'ui:widget': 'DialogCreatableSelectWidget'
        }
      },
      excludedDialogs: {
        items: {
          ...WITHOUT_LABEL,
          'ui:classNames': 'rjsf-array-select',
          'ui:widget': 'DialogCreatableSelectWidget'
        }
      },
      excludedStepIds: { items: WITHOUT_LABEL },
      provider: {
        ...{ system: TEXTAREA_WIDGET, stopwords: TEXTAREA_WIDGET },
        ...{
          type: HIDDEN_WIDGET,
          threshold: HIDDEN_WIDGET,
          showFollowUpQuestions: HIDDEN_WIDGET
        },
        thinkingMessage: TEXTAREA_WIDGET,
        prompt: { 'ui:widget': 'DefaultPromptEditorWidget', initialRowsCount: 12, defaultPrompt },

        llmChatService: {
          kind: 'llm-chat',
          action: 'Chat model response',
          'ui:widget': 'BotCloudServiceSelectWidget'
        },
        intentLlmChatService: {
          kind: 'llm-chat',
          action: 'Global NLP classifier',
          'ui:widget': 'BotCloudServiceSelectWidget'
        },
        finalLlmChatService: {
          kind: 'llm-chat',
          action: 'KB final response',
          'ui:widget': 'BotCloudServiceSelectWidget'
        },
        queryLlmChatService: {
          kind: 'llm-chat',
          action: 'KB search query',
          'ui:widget': 'BotCloudServiceSelectWidget'
        },
        messages: { items: { content: TEXTAREA_WIDGET } },
        tools: {
          items: {
            ...{ description: TEXTAREA_WIDGET, parameters: TEXTAREA_WIDGET },
            action: {
              type: HIDDEN_WIDGET,
              code: { 'ui:widget': 'EditorJavascriptWidget' },
              dialog: { 'ui:widget': 'DialogCreatableSelectWidget' }
            }
          }
        },
        knowledgeBases: isStandaloneKbEnabled
          ? {
              items: {
                ...WITHOUT_LABEL,
                'ui:classNames': 'rjsf-array-select',
                'ui:widget': 'KnowledgeBaseSelectWidget',
                id: { 'ui:widget': 'KnowledgeBaseSelectWidget' },
                retrieval: {
                  type: { 'ui:widget': 'hidden' },
                  queryLlmChatService: {
                    'ui:widget': 'BotCloudServiceSelectWidget',
                    kind: 'llm-chat',
                    action: 'KB search query'
                  }
                }
              }
            }
          : HIDDEN_WIDGET,
        sourceFormat: showSources !== 'inline' ? HIDDEN_WIDGET : {},
        // Required for now, due to a bug in rjsf: https://github.com/rjsf-team/react-jsonschema-form/issues/4000
        engine: { 'ui:title': 'Engine' },
        settings: { ignored: RADIO_WIDGET, priority: RANGE_WIDGET },

        ...getLlmRequestSettings(providerType!)
      },
      entities: {
        items: {
          type: HIDDEN_WIDGET,
          repository: isNlpEntityRepositoryEnabled ? {} : HIDDEN_WIDGET,
          schema: TEXTAREA_WIDGET,
          options: { items: { ...WITHOUT_LABEL, examples: { items: WITHOUT_LABEL } } }
        }
      },
      defaultDialog:
        !providerType || providerTypesWithHiddenDefaultDialog.includes(providerType)
          ? HIDDEN_WIDGET
          : { 'ui:widget': 'DialogCreatableSelectWidget' }
    }
  }
}

function getLlmRequestSettings(providerType: NlpProviderType) {
  const providerSchemaProperties = nlpProviderSchemas.find(
    provider => provider?.properties?.type?.const === providerType
  )?.properties

  return {
    temperature: {
      'ui:description': (
        <WithModelSpecificPropertyWarning>
          {providerSchemaProperties?.temperature?.description}
        </WithModelSpecificPropertyWarning>
      )
    },
    topP: {
      'ui:description': (
        <WithModelSpecificPropertyWarning>
          {providerSchemaProperties?.topP?.description}
        </WithModelSpecificPropertyWarning>
      )
    },
    maxTokens: {
      'ui:description': (
        <WithModelSpecificPropertyWarning>
          {providerSchemaProperties?.maxTokens?.description}
        </WithModelSpecificPropertyWarning>
      )
    },
    presencePenalty: {
      'ui:description': (
        <WithModelSpecificPropertyWarning>
          {providerSchemaProperties?.presencePenalty?.description}
        </WithModelSpecificPropertyWarning>
      )
    },
    frequencyPenalty: {
      'ui:description': (
        <WithModelSpecificPropertyWarning>
          {providerSchemaProperties?.frequencyPenalty?.description}
        </WithModelSpecificPropertyWarning>
      )
    },
    reasoningEffort: {
      'ui:description': (
        <WithModelSpecificPropertyWarning>
          {providerSchemaProperties?.reasoningEffort?.description}
        </WithModelSpecificPropertyWarning>
      )
    }
  }
}
